import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider, useLanguage, LANGS } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import { trackEvent } from "@/stats";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactsPage from "@/pages/ContactsPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPage from "@/pages/PrivacyPage";

function LangLayout() {
  const { lang } = useParams();
  const { setLang } = useLanguage();
  const valid = LANGS.includes(lang);
  useEffect(() => {
    if (valid) setLang(lang);
  }, [lang, valid, setLang]);
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    if (valid) trackEvent("pageview", { lang });
  }, [pathname, lang, valid]);
  if (!valid) return <Navigate to="/cs" replace />;
  return (
    <div className="min-h-screen bg-cream text-ink font-sans" data-testid={`layout-${lang}`}>
      <Navbar />
      <Outlet />
      <Footer />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/cs" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/:lang" element={<LangLayout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/cs" replace />} />
        </Routes>
        <CookieConsent />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </LanguageProvider>
  );
}
